import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { alertaCargandoProceso, alertaGeneral } from "../utils/alertas";
import MenuNav from "./VideoCall/MenuNav";
import CameraTest from "./CameraTest";
import api from "../utils/api";

const VideoCallDroxy = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isPatient } = location.state || { isPatient: true };
  const [showResume, setShowResume] = useState(false);
  const [inicio, setInicio] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);
  const [videocall, setVideocall] = useState(false);
  const [showCameraTest, setShowCameraTest] = useState(false);
  
  // Estados para AWS Chime
  const [isInMeeting, setIsInMeeting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentMeetingId, setCurrentMeetingId] = useState(null);
  const [currentAttendeeId, setCurrentAttendeeId] = useState(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [showVideoElements, setShowVideoElements] = useState(false);
  const [cameraStatus, setCameraStatus] = useState('checking'); // 'checking', 'active', 'error', 'denied'
  const [cameraError, setCameraError] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [availableDevices, setAvailableDevices] = useState({
    audioInputs: [],
    videoInputs: []
  });
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  
  // Referencias para elementos de video
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteVideoRefDesktop = useRef(null);
  const remoteVideoRefMobile = useRef(null);

  // Función para verificar elementos de video
  const ensureVideoElements = () => {
    const localExists = !!localVideoRef.current;
    const remoteExists = !!remoteVideoRef.current;
    const remoteDesktopExists = !!remoteVideoRefDesktop.current;
    const remoteMobileExists = !!remoteVideoRefMobile.current;
    
    console.log('🔍 Verificando elementos de video:', {
      localExists,
      remoteExists,
      remoteDesktopExists,
      remoteMobileExists,
      localElement: localVideoRef.current?.tagName,
      remoteElement: remoteVideoRef.current?.tagName
    });
    
    if (!localExists) {
      console.warn('⚠️ localVideoRef no está disponible');
    }
    if (!remoteExists && !remoteDesktopExists && !remoteMobileExists) {
      console.warn('⚠️ Ningún elemento remoteVideoRef disponible');
    }
    
    // Ser más permisivo - solo requerir que al menos uno exista
    return localExists || remoteExists || remoteDesktopExists || remoteMobileExists;
  };

  // Función para verificar y corregir la vinculación del video remoto
  const checkAndFixRemoteVideoBinding = async () => {
    try {
      if (!window.currentMeetingSession) {
        return;
      }
      
      const audioVideo = window.currentMeetingSession.audioVideo;
      const allTiles = audioVideo.getAllVideoTiles();
      
      // Intentar con todos los tiles remotos (no filtrar por active para ser más permisivo)
      let remoteTiles = allTiles.filter(tile => !tile.localTile && !tile.isContent);
      
      // Priorizar tiles activos si hay múltiples
      const activeRemoteTiles = remoteTiles.filter(tile => tile.active);
      if (activeRemoteTiles.length > 0) {
        remoteTiles = activeRemoteTiles;
        console.log('🔍 Priorizando tiles remotos activos');
      } else {
        console.log('🔍 No hay tiles remotos activos, usando todos los tiles remotos disponibles...');
      }
      
      console.log('🔍 Verificando vinculación de video remoto:', {
        totalTiles: allTiles.length,
        activeRemoteTiles: allTiles.filter(t => !t.localTile && !t.isContent && t.active).length,
        allRemoteTiles: allTiles.filter(t => !t.localTile && !t.isContent).length,
        remoteTilesFound: remoteTiles.length,
        remoteDesktopExists: !!remoteVideoRefDesktop.current,
        remoteMobileExists: !!remoteVideoRefMobile.current,
        remoteGenericExists: !!remoteVideoRef.current,
        isPatient: isPatient,
        tilesInfo: remoteTiles.map(t => ({
          tileId: t.tileId,
          active: t.active,
          boundAttendeeId: t.boundAttendeeId
        }))
      });
      
      if (remoteTiles.length > 0) {
        const remoteTile = remoteTiles[0]; // Usar el primer tile remoto
        
        // Función helper para vincular video remoto
        const bindRemoteVideo = (element, name) => {
          if (!element) {
            console.warn(`⚠️ Elemento ${name} no está disponible`);
            return false;
          }
          
          // Verificar que el elemento esté en el DOM
          if (!document.contains(element)) {
            console.warn(`⚠️ Elemento ${name} no está en el DOM`);
            return false;
          }
          
          try {
            // Verificar si el elemento ya tiene srcObject vinculado
            if (element.srcObject) {
              console.log(`✅ ${name} ya tiene srcObject`);
              return true;
            }
            
            audioVideo.bindVideoElement(remoteTile.tileId, element);
            console.log(`✅ Video remoto vinculado a ${name}, tileId: ${remoteTile.tileId}`);
            
            // Verificar después de un momento
            setTimeout(() => {
              if (element.srcObject) {
                console.log(`✅ Confirmado: ${name} tiene srcObject después de vinculación`);
              } else {
                console.warn(`⚠️ ${name} no tiene srcObject después de vinculación`);
              }
            }, 500);
            
            return true;
          } catch (error) {
            console.error(`❌ Error al vincular video remoto a ${name}:`, error);
            return false;
          }
        };
        
        // Detectar si estamos en desktop o mobile
        const isDesktop = window.innerWidth >= 768;
        
        let bound = false;
        
        console.log('📱 Verificando vinculación remota:', {
          isDesktop,
          isPatient,
          remoteDesktopRef: !!remoteVideoRefDesktop.current,
          remoteMobileRef: !!remoteVideoRefMobile.current,
          remoteGenericRef: !!remoteVideoRef.current
        });
        
        // Vincular a ambos elementos simultáneamente para asegurar que funcione
        // en cualquier dispositivo y cuando cambie el tamaño de la ventana
        
        // Intentar vincular a desktop
        if (remoteVideoRefDesktop.current) {
          bound = bindRemoteVideo(remoteVideoRefDesktop.current, 'desktop') || bound;
        }
        
        // También intentar vincular a mobile
        if (remoteVideoRefMobile.current) {
          bound = bindRemoteVideo(remoteVideoRefMobile.current, 'mobile') || bound;
        }
        
        // Fallback al ref genérico
        if (!bound && remoteVideoRef.current) {
          console.log('🔄 Intentando fallback a ref genérico...');
          bound = bindRemoteVideo(remoteVideoRef.current, 'genérico');
        }
        
        if (!bound) {
          console.warn('⚠️ No se pudo vincular video remoto a ningún elemento. Estado completo:', {
            tileId: remoteTile.tileId,
            active: remoteTile.active,
            boundAttendeeId: remoteTile.boundAttendeeId,
            remoteDesktopRef: !!remoteVideoRefDesktop.current,
            remoteMobileRef: !!remoteVideoRefMobile.current,
            remoteGenericRef: !!remoteVideoRef.current,
            isDesktop,
            isPatient
          });
        }
      } else {
        console.log('🔍 No hay tiles remotos disponibles todavía. Todos los tiles:', 
          allTiles.map(t => ({
            tileId: t.tileId,
            localTile: t.localTile,
            isContent: t.isContent,
            active: t.active
          }))
        );
      }
      
    } catch (error) {
      console.error('❌ Error en verificación de vinculación remota:', error);
    }
  };

  // Función mejorada para esperar elementos de video con retry
  const waitForVideoElements = async (maxAttempts = 20, delay = 500) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`⏳ Esperando elementos de video... intento ${attempt}/${maxAttempts}`);
      
      if (ensureVideoElements()) {
        console.log('✅ Elementos de video disponibles');
        return true;
      }
      
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    console.warn('⚠️ Elementos de video no disponibles después de todos los intentos');
    return false;
  };

  // Cargar dispositivos disponibles
  const loadAvailableDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      const videoInputs = devices.filter(device => device.kind === 'videoinput');
      
      setAvailableDevices({ audioInputs, videoInputs });
      
      if (audioInputs.length > 0 && !selectedAudioDevice) {
        setSelectedAudioDevice(audioInputs[0].deviceId);
      }
      if (videoInputs.length > 0 && !selectedVideoDevice) {
        setSelectedVideoDevice(videoInputs[0].deviceId);
      }
      
      return { audioInputs, videoInputs };
    } catch (error) {
      console.error('Error al cargar dispositivos:', error);
      return { audioInputs: [], videoInputs: [] };
    }
  };

  // Función para obtener stream de cámara directamente
  const getCameraStream = async () => {
    try {
      console.log('📹 Solicitando acceso a la cámara...');
      setCameraStatus('checking');
      setCameraError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: false
      });
      
      console.log('✅ Stream de cámara obtenido:', {
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length,
        active: stream.active
      });
      
      setCameraStatus('active');
      return stream;
    } catch (error) {
      console.error('❌ Error al obtener stream de cámara:', error);
      
      let errorMessage = 'Error desconocido al acceder a la cámara';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Acceso a la cámara denegado. Por favor, permite el acceso a la cámara.';
        setCameraStatus('denied');
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No se encontró ninguna cámara en el dispositivo.';
        setCameraStatus('error');
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'La cámara está siendo utilizada por otra aplicación.';
        setCameraStatus('error');
      } else {
        setCameraStatus('error');
      }
      
      setCameraError(errorMessage);
      throw error;
    }
  };

  // Función para asignar stream directamente al elemento de video
  const assignStreamToVideo = async (videoRef, stream) => {
    if (!videoRef.current) {
      console.warn('⚠️ Elemento de video no disponible para asignar stream');
      return false;
    }

    try {
      console.log('🔄 Asignando stream al elemento de video...');
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.autoplay = true;
      videoRef.current.playsInline = true;
      videoRef.current.controls = false;
      
      // Forzar reproducción
      await videoRef.current.play();
      console.log('✅ Stream asignado y reproduciendo');
      
      // Verificar que el video esté funcionando
      setTimeout(() => {
        const video = videoRef.current;
        console.log('🔍 Estado del video después de asignación:', {
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          readyState: video.readyState,
          paused: video.paused,
          muted: video.muted,
          hasSrcObject: !!video.srcObject
        });
      }, 1000);
      
      return true;
    } catch (error) {
      console.error('❌ Error al asignar stream al video:', error);
      return false;
    }
  };

  // Función para asignar stream SOLO al video local del paciente (Tú)
  const assignStreamToAllPatientVideos = async (stream) => {
    console.log('🔄 Asignando stream SOLO al elemento local del paciente...');
    
    const target = localVideoRef.current;
    if (!target) {
      console.warn('⚠️ localVideoRef no disponible');
      return false;
    }
    try {
      target.srcObject = stream;
      target.muted = true;
      target.autoplay = true;
      target.playsInline = true;
      target.controls = false;
      await target.play();
      console.log('✅ Stream asignado a localVideoRef');
      return true;
    } catch (error) {
      console.error('❌ Error al asignar stream a localVideoRef:', error);
      return false;
    }
  };

  // Función específica para forzar cámara en PC
  const forceCameraOnPC = async () => {
    if (!isPatient) return;
    
    console.log('🖥️ FORZANDO CÁMARA EN PC...');
    
    try {
      // Obtener stream de cámara
      const stream = await getCameraStream();
      console.log('🖥️ Stream obtenido, asignando a elementos de PC...');
      
      // Preferir el video local por referencia
      if (localVideoRef.current) {
        console.log('🖥️ Asignando stream al video local (localVideoRef) en PC...');
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.muted = true;
        localVideoRef.current.autoplay = true;
        localVideoRef.current.playsInline = true;
        localVideoRef.current.controls = false;
        await localVideoRef.current.play();
        setCameraStatus('active');
        setIsVideoEnabled(true);
        console.log('✅ Cámara forzada en PC al video local');
        return;
      }

      // Fallback: buscar específicamente el video marcado como data-self
      const desktopLayout = document.querySelector('.hidden.md\\:flex');
      if (desktopLayout) {
        const selfVideo = desktopLayout.querySelector('video[data-self="true"]');
        if (selfVideo) {
          console.log('🖥️ Asignando stream al video con data-self="true"...');
          selfVideo.srcObject = stream;
          selfVideo.muted = true;
          selfVideo.autoplay = true;
          selfVideo.playsInline = true;
          selfVideo.controls = false;
          await selfVideo.play();
          setCameraStatus('active');
          setIsVideoEnabled(true);
          console.log('✅ Cámara forzada en PC al video data-self');
          return;
        }
      }

      // Último recurso: método normal
      console.log('🖥️ No se encontró video específico, usando método normal...');
      await assignStreamToAllPatientVideos(stream);
    } catch (error) {
      console.error('❌ Error al forzar cámara en PC:', error);
      setCameraStatus('error');
      setCameraError(error.message);
    }
  };

  // Función SUPER AGRESIVA - Múltiples estrategias hasta que aparezca la cámara
  const forceCameraWithAllStrategies = async () => {
    if (!isPatient) return;
    
    console.log('🚀 INICIANDO ESTRATEGIA MULTIPLE DE ASIGNACIÓN DE CÁMARA...');
    
    try {
      const stream = await getCameraStream();
      console.log('✅ Stream obtenido, iniciando múltiples estrategias...');
      
      let success = false;
      let attempts = 0;
      const maxAttempts = 10;
      
      const assignStreamToVideo = async (videoElement, strategyName) => {
        try {
          if (!videoElement) return false;
          
          videoElement.srcObject = stream;
          videoElement.muted = true;
          videoElement.autoplay = true;
          videoElement.playsInline = true;
          videoElement.controls = false;
          videoElement.setAttribute('autoplay', '');
          videoElement.setAttribute('playsinline', '');
          videoElement.setAttribute('muted', '');
          
          await videoElement.play();
          
          // Verificar que realmente está funcionando
          setTimeout(() => {
            if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
              console.log(`✅ ESTRATEGIA "${strategyName}" EXITOSA - Video funcionando`);
              success = true;
            }
          }, 500);
          
          return true;
        } catch (error) {
          console.warn(`⚠️ Estrategia "${strategyName}" falló:`, error);
          return false;
        }
      };
      
      // ESTRATEGIA 1: Referencias React directas (solo local para paciente)
      console.log('📋 ESTRATEGIA 1: Referencias React directas');
      if (localVideoRef.current) {
        await assignStreamToVideo(localVideoRef.current, 'React localVideoRef');
      }
      
      // ESTRATEGIA 2: Buscar video por data-self
      console.log('📋 ESTRATEGIA 2: Buscar video por data-self');
      const selfVideoDom = document.querySelector('video[data-self="true"]');
      if (selfVideoDom) {
        await assignStreamToVideo(selfVideoDom, 'DOM data-self video');
      }
      
      // ESTRATEGIA 3: Layout de escritorio -> solo data-self
      console.log('📋 ESTRATEGIA 3: Layout de escritorio específico(data-self)');
      const desktopLayout = document.querySelector('.hidden.md\\:flex');
      if (desktopLayout) {
        const desktopSelf = desktopLayout.querySelector('video[data-self="true"]');
        if (desktopSelf) {
          await assignStreamToVideo(desktopSelf, 'Desktop data-self video');
        }
      }
      
      // ESTRATEGIA 4: Buscar por clase específica de video (filtrando data-self)
      console.log('📋 ESTRATEGIA 4: Videos por clase (data-self)');
      const videosByClass = document.querySelectorAll('video.object-cover[data-self="true"]');
      videosByClass.forEach((video, index) => {
        assignStreamToVideo(video, `Class data-self video ${index + 1}`);
      });
      
      // ESTRATEGIA 5: Buscar dentro del contenedor de video (data-self)
      console.log('📋 ESTRATEGIA 5: Videos dentro del contenedor principal (data-self)');
      const videoContainer = document.querySelector('.flex-1.relative.overflow-hidden');
      if (videoContainer) {
        const containerSelf = videoContainer.querySelectorAll('video[data-self="true"]');
        containerSelf.forEach((video, index) => {
          assignStreamToVideo(video, `Container data-self video ${index + 1}`);
        });
      }
      
      // ESTRATEGIA 6: Buscar videos con autoplay (data-self)
      console.log('📋 ESTRATEGIA 6: Videos con autoplay (data-self)');
      const autoplayVideos = document.querySelectorAll('video[autoplay][data-self="true"]');
      autoplayVideos.forEach((video, index) => {
        assignStreamToVideo(video, `Autoplay data-self video ${index + 1}`);
      });
      
      // ESTRATEGIA 7: Reintentos periódicos hasta que funcione
      console.log('📋 ESTRATEGIA 7: Reintentos periódicos');
      const retryInterval = setInterval(async () => {
        attempts++;
        console.log(`🔄 Reintento ${attempts}/${maxAttempts}...`);
        
        // Verificar si algún video está funcionando
        const allVideosCheck = document.querySelectorAll('video');
        let anyWorking = false;
        
        allVideosCheck.forEach((video) => {
          if (video.srcObject && video.videoWidth > 0 && video.videoHeight > 0) {
            anyWorking = true;
          } else if (video.srcObject) {
            // Reintentar asignación
            assignStreamToVideo(video, `Retry ${attempts}`);
          }
        });
        
        if (anyWorking || attempts >= maxAttempts) {
          clearInterval(retryInterval);
          if (anyWorking) {
            console.log('✅ CÁMARA FUNCIONANDO DESPUÉS DE REINTENTOS');
            setCameraStatus('active');
            setIsVideoEnabled(true);
          }
        }
      }, 1000);
      
      // Actualizar estado si hay éxito inicial
      setTimeout(() => {
        if (success) {
          setCameraStatus('active');
          setIsVideoEnabled(true);
          console.log('✅ CÁMARA ACTIVADA CON ÉXITO');
        }
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error en estrategia múltiple:', error);
      setCameraStatus('error');
      setCameraError(error.message);
    }
  };

  // Función principal para unirse a la reunión (declarada antes del useEffect)
  const joinMeeting = async (meetingId, attendeeData, meetingData = null) => {
    try {
      console.log('🚀 Iniciando joinMeeting con:', { meetingId, attendeeData, meetingData });
      
      // Esperar a que los elementos de video estén disponibles
      const elementsReady = await waitForVideoElements(15, 300);
      if (!elementsReady) {
        console.warn('⚠️ Elementos de video no disponibles, pero continuando...');
      }
      
      // La cámara ya se activó inmediatamente en useEffect, no duplicar aquí
      if (isPatient) {
        console.log('👤 Es paciente - cámara ya activada en useEffect');
      }
      
      await setupChimeMeeting(meetingData, attendeeData);
      console.log('✅ joinMeeting completado exitosamente');
    } catch (error) {
      console.error('❌ Error en joinMeeting:', error);
      throw error;
    }
  };

  // Función para configurar la reunión de Chime (declarada antes del useEffect)
  const setupChimeMeeting = async (meetingData, attendeeData) => {
    try {
      console.log('🔧 Configurando reunión de Chime...');
      console.log('📋 Meeting Data raw:', JSON.stringify(meetingData, null, 2));
      console.log('📋 Attendee Data raw:', JSON.stringify(attendeeData, null, 2));
      
      const { ConsoleLogger, DefaultDeviceController, DefaultMeetingSession, LogLevel, MeetingSessionConfiguration } =
        await import('amazon-chime-sdk-js');

      const findProperty = (obj, propNames) => {
        if (!obj || typeof obj !== 'object') return null;
        for (const name of propNames) {
          if (obj[name] !== undefined) return obj[name];
        }
        for (const key in obj) {
          if (typeof obj[key] === 'object') {
            const result = findProperty(obj[key], propNames);
            if (result !== null) return result;
          }
        }
        return null;
      };

      // Extraer datos de la reunión (igual que VideoCallAWS)
      const buildMeetingConfig = () => {
        const meetingId = findProperty(meetingData, ['MeetingId', 'meetingId', 'meeting_id', 'id']);
        const mediaPlacement = findProperty(meetingData, ['MediaPlacement', 'mediaPlacement', 'media_placement']);

        const audioHostUrl = findProperty(
          mediaPlacement || meetingData,
          ['AudioHostUrl', 'audioHostUrl', 'audio_host_url']
        );
        const audioFallbackUrl = findProperty(
          mediaPlacement || meetingData,
          ['AudioFallbackUrl', 'audioFallbackUrl', 'audio_fallback_url']
        );
        const signalingUrl = findProperty(
          mediaPlacement || meetingData,
          ['SignalingUrl', 'signalingUrl', 'signaling_url']
        );
        const turnControlUrl = findProperty(
          mediaPlacement || meetingData,
          ['TurnControlUrl', 'turnControlUrl', 'turn_control_url']
        );

        console.log('📋 Extraído de meeting:', {
          meetingId,
          audioHostUrl,
          audioFallbackUrl,
          signalingUrl,
          turnControlUrl
        });

        if (!meetingId) {
          throw new Error('ID de reunión no disponible en los datos');
        }
        if (!signalingUrl) {
          throw new Error('URL de señalización no disponible');
        }

        return {
          Meeting: {
            MeetingId: meetingId,
            MediaPlacement: {
              AudioHostUrl: audioHostUrl || '',
              AudioFallbackUrl: audioFallbackUrl || '',
              SignalingUrl: signalingUrl,
              TurnControlUrl: turnControlUrl || ''
            }
          }
        };
      };

      // Extraer datos del asistente (igual que VideoCallAWS)
      const buildAttendeeConfig = () => {
        const attendeeId = findProperty(attendeeData, ['AttendeeId', 'attendeeId', 'attendee_id', 'id']);
        const joinToken = findProperty(attendeeData, ['JoinToken', 'joinToken', 'join_token', 'token']);

        console.log('📋 Extraído de attendee:', {
          attendeeId,
          joinToken
        });

        if (!attendeeId || !joinToken) {
          throw new Error('Datos de asistente incompletos (falta ID o token)');
        }

        return {
          Attendee: {
            AttendeeId: attendeeId,
            JoinToken: joinToken
          }
        };
      };

      const formattedMeetingData = buildMeetingConfig();
      const formattedAttendeeData = buildAttendeeConfig();

      console.log('✅ Formatted meeting data:', JSON.stringify(formattedMeetingData, null, 2));
      console.log('✅ Formatted attendee data:', JSON.stringify(formattedAttendeeData, null, 2));

      const logger = new ConsoleLogger('ChimeMeetingLogs', LogLevel.OFF);
      const deviceController = new DefaultDeviceController(logger);
      const configuration = new MeetingSessionConfiguration(formattedMeetingData, formattedAttendeeData);
      const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);
      
      // Guardar la sesión en window para acceso global
      window.currentMeetingSession = meetingSession;
      
      console.log('✅ Meeting session creada');

      meetingSession.audioVideo.addObserver({
        // Observer para cuando los participantes remotos se unen
        remoteVideoSourcesDidChange: (videoSources) => {
          console.log('👥👥👥 PARTICIPANTES REMOTOS CAMBIARON:', {
            videoSources: videoSources,
            count: videoSources.length,
            isPatient: isPatient
          });
          
          // Cuando hay participantes remotos, verificar tiles inmediatamente
          if (videoSources.length > 0) {
            console.log('🔄 Participantes remotos detectados, verificando tiles...');
            setTimeout(() => {
              checkAndFixRemoteVideoBinding();
            }, 500);
          }
        },
        
        videoTileDidUpdate: tileState => {
          console.log('🎥 Video tile actualizado:', {
            tileId: tileState.tileId,
            localTile: tileState.localTile,
            isContent: tileState.isContent,
            active: tileState.active,
            localExists: !!localVideoRef.current,
            remoteExists: !!remoteVideoRef.current,
            isPatient: isPatient
          });
          
          // NO ignorar tiles para pacientes - necesitamos procesar tiles remotos para ver al doctor
          // Si el tile no está activo, programar un intento pero continuar
          if (!tileState.active) {
            console.log('⏸️ Tile no está activo todavía, pero continuando...');
            // No retornar aquí - intentar vincular de todos modos
          }
          
          // Si es un mosaico local (tu propio video)
          if (tileState.localTile) {
            console.log('🔍 Detectado video local, tileId:', tileState.tileId);
            if (localVideoRef.current) {
              console.log('✅ Elemento localVideoRef disponible, vinculando...');
              try {
                meetingSession.audioVideo.bindVideoElement(tileState.tileId, localVideoRef.current);
                console.log('✅ Video local vinculado exitosamente al elemento HTML');
                
                // Verificar que la vinculación funcionó
                setTimeout(() => {
                  const videoElement = localVideoRef.current;
                  if (videoElement && videoElement.srcObject) {
                    console.log('✅ Confirmado: Elemento de video local tiene srcObject');
                  } else {
                    console.warn('⚠️ Elemento de video local no tiene srcObject después de vinculación');
                  }
                }, 500);
                
              } catch (error) {
                console.error('❌ Error al vincular video local:', error);
              }
            } else {
              console.warn('⚠️ localVideoRef.current es null - no se puede vincular video local');
            }
          }
          // Si es un mosaico remoto (video de otro participante)
          else if (!tileState.isContent && !tileState.localTile) {
            console.log('🔍🔍🔍 DETECTADO VIDEO REMOTO - VINCULANDO:', {
              tileId: tileState.tileId,
              active: tileState.active,
              localTile: tileState.localTile,
              isContent: tileState.isContent,
              boundAttendeeId: tileState.boundAttendeeId,
              boundExternalUserId: tileState.boundExternalUserId,
              isPatient: isPatient,
              remoteDesktopExists: !!remoteVideoRefDesktop.current,
              remoteMobileExists: !!remoteVideoRefMobile.current,
              remoteGenericExists: !!remoteVideoRef.current
            });
            
            // Si el tile no está activo, programar un intento pero no bloquear
            if (!tileState.active) {
              console.log('⏸️ Tile remoto no está activo todavía, programando intento...');
              // Intentar vincular cuando se active
              setTimeout(() => {
                checkAndFixRemoteVideoBinding();
              }, 1000);
              // NO retornar aquí - continuar con el intento de vinculación de todos modos
            }
            
            // Función helper para vincular video
            const bindVideo = (element, name) => {
              if (!element) {
                console.warn(`⚠️ Elemento ${name} no está disponible`);
                return false;
              }
              
              // Verificar que el elemento esté en el DOM
              if (!document.contains(element)) {
                console.warn(`⚠️ Elemento ${name} no está en el DOM`);
                return false;
              }
              
              try {
                meetingSession.audioVideo.bindVideoElement(
                  tileState.tileId,
                  element
                );
                console.log(`✅ Video remoto vinculado exitosamente a ${name}, tileId: ${tileState.tileId}`);
                
                // Verificar después de un momento que funcionó
                setTimeout(() => {
                  if (element.srcObject) {
                    console.log(`✅ Confirmado: ${name} tiene srcObject después de vinculación`);
                  } else {
                    console.warn(`⚠️ ${name} no tiene srcObject después de vinculación, reintentando...`);
                    // Reintentar
                    setTimeout(() => {
                      try {
                        meetingSession.audioVideo.bindVideoElement(tileState.tileId, element);
                        console.log(`🔄 Reintento de vinculación a ${name}`);
                      } catch (retryError) {
                        console.error(`❌ Error en reintento a ${name}:`, retryError);
                      }
                    }, 500);
                  }
                }, 500);
                
                return true;
              } catch (error) {
                console.error(`❌ Error al vincular video remoto a ${name}:`, error);
                return false;
              }
            };
            
            // Detectar si estamos en desktop o mobile usando window.innerWidth
            const isDesktop = window.innerWidth >= 768; // md breakpoint de Tailwind
            
            console.log('📱 Vinculando video remoto:', {
              dispositivo: isDesktop ? 'desktop' : 'mobile',
              isPatient: isPatient,
              remoteDesktopRef: !!remoteVideoRefDesktop.current,
              remoteMobileRef: !!remoteVideoRefMobile.current,
              remoteGenericRef: !!remoteVideoRef.current
            });
            
            let bound = false;
            
            // Vincular a ambos elementos (desktop y mobile) simultáneamente
            // AWS Chime permite vincular el mismo tile a múltiples elementos
            // Esto asegura que funcione independientemente de qué elemento esté visible
            
            // Intentar vincular a desktop primero (si está disponible)
            if (remoteVideoRefDesktop.current) {
              bound = bindVideo(remoteVideoRefDesktop.current, 'desktop') || bound;
            }
            
            // También intentar vincular a mobile (si está disponible)
            // Esto es útil cuando hay cambios de tamaño de ventana o en mobile
            if (remoteVideoRefMobile.current) {
              bound = bindVideo(remoteVideoRefMobile.current, 'mobile') || bound;
            }
            
            // Si ninguno funcionó, intentar con el ref genérico como fallback
            if (!bound && remoteVideoRef.current) {
              console.log('🔄 Intentando fallback a ref genérico...');
              bound = bindVideo(remoteVideoRef.current, 'genérico (fallback)');
            }
            
            if (!bound) {
              console.warn('⚠️ No se pudo vincular video remoto a ningún elemento. Estado:', {
                tileId: tileState.tileId,
                active: tileState.active,
                remoteDesktopRef: !!remoteVideoRefDesktop.current,
                remoteMobileRef: !!remoteVideoRefMobile.current,
                remoteGenericRef: !!remoteVideoRef.current
              });
            }
            
            // También intentar vincular con la función de verificación periódica como respaldo
            setTimeout(() => {
              checkAndFixRemoteVideoBinding();
            }, 500);
          }
        }
      });

      console.log('🚀 Iniciando sesión de audio/video...');
      await meetingSession.audioVideo.start();
      console.log('✅ Sesión iniciada correctamente');
      
      // Verificar tiles remotos inmediatamente después de iniciar
      setTimeout(() => {
        console.log('🔄 Verificación inicial de tiles remotos...');
        checkAndFixRemoteVideoBinding();
      }, 1000);
      
      // Verificar tiles remotos periódicamente después de iniciar
      const remoteCheckInterval = setInterval(() => {
        if (window.currentMeetingSession === meetingSession) {
          checkAndFixRemoteVideoBinding();
        } else {
          clearInterval(remoteCheckInterval);
        }
      }, 1500);
      
      // Limpiar el intervalo después de 30 segundos (luego la verificación periódica del useEffect lo manejará)
      setTimeout(() => {
        clearInterval(remoteCheckInterval);
        console.log('🛑 Deteniendo verificación inicial de tiles remotos');
      }, 30000);
      
      const audioInputs = await meetingSession.audioVideo.listAudioInputDevices();
      console.log('🎤 Dispositivos de audio disponibles:', audioInputs.length);
      if (audioInputs.length > 0) {
        await meetingSession.audioVideo.startAudioInput(audioInputs[0].deviceId);
        console.log('✅ Audio iniciado');
      }

      const videoInputs = await meetingSession.audioVideo.listVideoInputDevices();
      console.log('📹 Dispositivos de video disponibles:', videoInputs.length);
      if (videoInputs.length > 0) {
        await meetingSession.audioVideo.startVideoInput(videoInputs[0].deviceId);
        await meetingSession.audioVideo.startLocalVideoTile();
        setIsVideoEnabled(true);
        console.log('✅ Video iniciado');
        
        // Forzar la activación del video local después de un breve delay
        setTimeout(async () => {
          try {
            console.log('🔄 Forzando activación de video local...');
            await meetingSession.audioVideo.startLocalVideoTile();
            console.log('✅ Video local forzado activado');
          } catch (error) {
            console.error('❌ Error al forzar video local:', error);
          }
        }, 1000);
      }

      let audioElement = document.getElementById('meeting-audio');
      if (!audioElement) {
        audioElement = document.createElement('audio');
        audioElement.id = 'meeting-audio';
        audioElement.style.display = 'none';
        document.body.appendChild(audioElement);
        console.log('✅ Elemento de audio creado');
      }
      await meetingSession.audioVideo.bindAudioElement(audioElement);
      console.log('✅ Audio vinculado');

      window.currentMeetingSession = meetingSession;
      console.log('✅ Reunión configurada exitosamente');
      
      // Función de depuración para verificar elementos de video
      const debugVideoElements = () => {
        console.log('🔍 Estado de elementos de video:', {
          localVideoRef: {
            exists: !!localVideoRef.current,
            srcObject: localVideoRef.current?.srcObject ? 'SÍ' : 'NO',
            videoWidth: localVideoRef.current?.videoWidth || 0,
            videoHeight: localVideoRef.current?.videoHeight || 0,
            readyState: localVideoRef.current?.readyState || 'N/A'
          },
          remoteVideoRef: {
            exists: !!remoteVideoRef.current,
            srcObject: remoteVideoRef.current?.srcObject ? 'SÍ' : 'NO',
            videoWidth: remoteVideoRef.current?.videoWidth || 0,
            videoHeight: remoteVideoRef.current?.videoHeight || 0,
            readyState: remoteVideoRef.current?.readyState || 'N/A'
          }
        });
      };
      
      // Ejecutar depuración después de un delay
      setTimeout(debugVideoElements, 2000);
      
      // Depuración adicional para pacientes
      if (isPatient) {
        setTimeout(() => {
          console.log('🔍 DIAGNÓSTICO COMPLETO PARA PACIENTE:');
          console.log('📊 Estado de la cámara:', cameraStatus);
          console.log('📊 Error de cámara:', cameraError);
          console.log('📊 Video habilitado:', isVideoEnabled);
          console.log('📊 Elemento localVideoRef:', {
            exists: !!localVideoRef.current,
            srcObject: !!localVideoRef.current?.srcObject,
            videoWidth: localVideoRef.current?.videoWidth,
            videoHeight: localVideoRef.current?.videoHeight,
            readyState: localVideoRef.current?.readyState,
            paused: localVideoRef.current?.paused
          });
          console.log('📊 Dispositivos de media disponibles:', {
            hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia,
            hasEnumerateDevices: !!navigator.mediaDevices?.enumerateDevices
          });
        }, 3000);
      }
      
      // Fallback: Intentar vincular video local manualmente si no se ha vinculado automáticamente
      setTimeout(async () => {
        if (localVideoRef.current && !localVideoRef.current.srcObject) {
          console.log('🔄 Fallback: Intentando vincular video local manualmente...');
          try {
            // Obtener el stream de video local directamente
            const stream = await navigator.mediaDevices.getUserMedia({ 
              video: true, 
              audio: false 
            });
            localVideoRef.current.srcObject = stream;
            localVideoRef.current.muted = true;
            localVideoRef.current.autoplay = true;
            localVideoRef.current.playsInline = true;
            console.log('✅ Video local vinculado manualmente como fallback');
          } catch (error) {
            console.error('❌ Error en fallback de video local:', error);
          }
        }
      }, 3000);
      
      return { meetingSession, chime: meetingSession.audioVideo };
    } catch (error) {
      console.error('❌ Error setting up Chime meeting:', error);
      throw error;
    }
  };

  // Función para manejar la unión a la reunión de AWS (declarada con useCallback ANTES del useEffect)
  const handleJoinMeeting = useCallback(async (appointmentId) => {
    try {
      console.log('🔵 handleJoinMeeting llamado con appointmentId:', appointmentId);
      
      // Verificar que el API Key esté configurado antes de hacer cualquier llamada
      let apiKey = localStorage.getItem('apiKey');
      console.log('🔐 Verificando API Key antes de unirse:', {
        apiKeyPresent: !!apiKey,
        apiKeyPreview: apiKey ? apiKey.substring(0, 10) + '...' : 'NO HAY'
      });
      
      if (!apiKey) {
        // Intentar obtener de la URL
        const urlParams = new URLSearchParams(window.location.search);
        const apiKeyFromUrl = urlParams.get('apiKey');
        if (apiKeyFromUrl) {
          console.log('🔐 API Key encontrada en URL, guardándola...');
          api.setApiKey(apiKeyFromUrl);
          apiKey = apiKeyFromUrl;
        } else {
          console.error('❌ ERROR: No hay API Key disponible. No se puede unir a la reunión.');
          throw new Error('API Key requerida. Por favor, incluye el parámetro apiKey en la URL.');
        }
      }
      
      setIsLoading(true);
      setError(null);
      
      console.log('📱 Cargando dispositivos disponibles...');
      await loadAvailableDevices();
      
      console.log('🌐 Llamando a api.getPublicMeeting...');
      const meetingResponse = await api.getPublicMeeting(appointmentId);
      console.log('📥 Respuesta de getPublicMeeting:', meetingResponse);
      
      if (!meetingResponse.success) {
        throw new Error(meetingResponse.error || 'Error al obtener información de la reunión');
      }
      
      // Estructura de datos del backend:
      // { success: true, data: { meetingId, externalMeetingId, meetingData: { MeetingId, MediaPlacement, ... }, ... } }
      const responseData = meetingResponse.data;
      console.log('🔍 Datos de la reunión obtenidos (Droxy):', responseData);
      
      // Los datos de AWS Chime están en meetingData.meetingData
      const meetingData = responseData.meetingData || responseData;
      const awsMeetingId = responseData.meetingId || meetingData.MeetingId || meetingData.meetingId;
      
      console.log('🔍 AWS Meeting ID:', awsMeetingId);
      console.log('🔍 Meeting Data completo:', meetingData);
      
      // Verificar URLs de la reunión
      if (meetingData && meetingData.MediaPlacement) {
        console.log('🔍 URLs de MediaPlacement:', {
          audioHostUrl: meetingData.MediaPlacement.AudioHostUrl,
          signalingUrl: meetingData.MediaPlacement.SignalingUrl,
          turnControlUrl: meetingData.MediaPlacement.TurnControlUrl
        });
        
        // Verificar si son URLs de ejemplo
        if (meetingData.MediaPlacement.SignalingUrl && meetingData.MediaPlacement.SignalingUrl.includes('example.com')) {
          console.error('❌ ERROR: La reunión tiene URLs de ejemplo, no URLs reales de AWS Chime');
          setError('La reunión no está configurada correctamente en AWS Chime. URLs de ejemplo detectadas.');
          return;
        }
      }
      
      setCurrentMeetingId(awsMeetingId);
      
      console.log('🌐 Llamando a api.joinPublicMeeting...');
      const joinResponse = await api.joinPublicMeeting(appointmentId, {
        patientName: isPatient ? 'Paciente Droxy' : 'Doctor Droxy',
        patientId: appointmentId
      });
      console.log('📥 Respuesta de joinPublicMeeting:', joinResponse);
      
      if (!joinResponse.success) {
        throw new Error(joinResponse.error || 'Error al unirse a la reunión');
      }
      
      const attendeeData = joinResponse.data?.attendee || joinResponse.data?.Attendee;
      if (!attendeeData) {
        throw new Error('No se recibieron datos de asistente del backend');
      }
      
      const attendeeId = attendeeData.AttendeeId || attendeeData.attendeeId || attendeeData.id;
      if (!attendeeId) {
        throw new Error('El asistente no tiene AttendeeId válido');
      }
      
      setCurrentAttendeeId(attendeeId);
      
      // Mostrar elementos de video ANTES de unirse a la reunión
      setShowVideoElements(true);
      setVideocall(true);
      setInicio(true);
      
      console.log('🎬 Mostrando elementos de video antes de unirse...');
      console.log('🔍 Meeting ID:', awsMeetingId);
      console.log('🔍 Attendee Data:', attendeeData);
      console.log('🔍 Meeting Data para AWS Chime:', meetingData);
      await joinMeeting(awsMeetingId, attendeeData, meetingData);
      
      setIsInMeeting(true);
      console.log('✅ Unido a la reunión exitosamente');
      
    } catch (error) {
      console.error('❌ Error al unirse a la reunión (Droxy):', error);
      setError('Error al unirse a la reunión: ' + error.message);
      alertaGeneral({
        titulo: "Error",
        messageHtml: 'Error al unirse a la videoconsulta: ' + error.message,
        isError: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [isPatient]);

  // Función para activar cámara inmediatamente
  const activateCameraImmediately = async () => {
    if (!isPatient) return;
    
    try {
      console.log('🚀 ACTIVACIÓN INMEDIATA DE CÁMARA PARA PACIENTE');
      console.log('🔍 Estado inicial:', {
        isPatient,
        localVideoRefExists: !!localVideoRef.current,
        cameraStatus,
        hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia
      });
      
      setCameraStatus('checking');
      
      // Esperar a que el elemento de video esté disponible
      let attempts = 0;
      while (!localVideoRef.current && attempts < 20) {
        console.log(`⏳ Esperando elemento de video... intento ${attempts + 1}/20`);
        await new Promise(resolve => setTimeout(resolve, 200));
        attempts++;
      }
      
      if (!localVideoRef.current) {
        console.error('❌ Elemento de video no disponible después de esperar');
        setCameraStatus('error');
        setCameraError('Elemento de video no disponible');
        return;
      }
      
      console.log('✅ Elemento de video disponible, obteniendo stream...');
      
      // USAR ESTRATEGIA MULTIPLE - Esto intentará TODAS las formas posibles
      console.log('🚀 Usando estrategia múltiple de asignación...');
      await forceCameraWithAllStrategies();
      
      // También usar los métodos anteriores como respaldo adicional
      console.log('🖥️ Intentando activación específica para PC...');
      await forceCameraOnPC();
      
      // También usar el método normal como respaldo
      const stream = await getCameraStream();
      const assigned = await assignStreamToAllPatientVideos(stream);
      
      if (assigned) {
        console.log('✅ CÁMARA ACTIVADA EXITOSAMENTE EN TODOS LOS ELEMENTOS');
        setCameraStatus('active');
        setIsVideoEnabled(true);
        
        // Verificación adicional después de 1 segundo
        setTimeout(() => {
          console.log('🔍 Verificación final de TODOS los videos:');
          console.log('📱 localVideoRef:', {
            exists: !!localVideoRef.current,
            srcObject: !!localVideoRef.current?.srcObject,
            videoWidth: localVideoRef.current?.videoWidth,
            videoHeight: localVideoRef.current?.videoHeight,
            readyState: localVideoRef.current?.readyState,
            paused: localVideoRef.current?.paused
          });
          console.log('📱 remoteVideoRef:', {
            exists: !!remoteVideoRef.current,
            srcObject: !!remoteVideoRef.current?.srcObject,
            videoWidth: remoteVideoRef.current?.videoWidth,
            videoHeight: remoteVideoRef.current?.videoHeight,
            readyState: remoteVideoRef.current?.readyState,
            paused: remoteVideoRef.current?.paused
          });
        }, 1000);
      } else {
        console.error('❌ Error al asignar stream a videos');
        setCameraStatus('error');
        setCameraError('Error al asignar stream de video');
      }
    } catch (error) {
      console.error('❌ Error en activación inmediata de cámara:', error);
      setCameraStatus('error');
      setCameraError(error.message);
    }
  };

  useEffect(() => {
    console.log('🔄 useEffect ejecutándose...', { appointmentId, isPatient });
    
    // Activar cámara inmediatamente para pacientes - con delay para asegurar que el DOM esté listo
    if (isPatient) {
      console.log('👤 Es paciente - programando activación de cámara...');
      // Delay para asegurar que los elementos de video estén renderizados
      setTimeout(() => {
        console.log('👤 Ejecutando activación de cámara después del delay...');
        activateCameraImmediately();
      }, 1000);
    }
    
    // Obtener API Key de la URL (query string o hash) o localStorage
    const urlParams = new URLSearchParams(window.location.search);
    let apiKeyFromUrl = urlParams.get('apiKey');
    
    // Si no está en query string, verificar en el hash
    if (!apiKeyFromUrl && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      apiKeyFromUrl = hashParams.get('apiKey');
    }
    
    // También verificar el estado de navegación (si viene de otra página)
    const apiKeyFromState = location.state?.apiKey;
    
    console.log('🔐 Verificando API Key en useEffect:', {
      apiKeyFromUrl: apiKeyFromUrl ? apiKeyFromUrl.substring(0, 10) + '...' : 'NO HAY EN URL',
      apiKeyFromState: apiKeyFromState ? apiKeyFromState.substring(0, 10) + '...' : 'NO HAY EN STATE',
      apiKeyFromStorage: localStorage.getItem('apiKey') ? 'PRESENTE' : 'NO HAY',
      fullUrl: window.location.href,
      search: window.location.search,
      hash: window.location.hash
    });
    
    // Priorizar: State > URL > localStorage
    const finalApiKey = apiKeyFromState || apiKeyFromUrl || localStorage.getItem('apiKey');
    
    // Si hay API Key en cualquier fuente, guardarla en localStorage INMEDIATAMENTE
    if (apiKeyFromState || apiKeyFromUrl) {
      const keyToSave = apiKeyFromState || apiKeyFromUrl;
      console.log('🔐 Guardando API Key desde', apiKeyFromState ? 'STATE' : 'URL', 'a localStorage...');
      api.setApiKey(keyToSave);
      
      // Verificar que se guardó correctamente
      const savedApiKey = localStorage.getItem('apiKey');
      console.log('🔐 API Key guardada, verificación:', {
        guardada: !!savedApiKey,
        coincide: savedApiKey === keyToSave,
        preview: savedApiKey ? savedApiKey.substring(0, 10) + '...' : 'NO SE GUARDÓ'
      });
    } else if (!finalApiKey) {
      // Verificar si hay API Key en localStorage
      const apiKeyFromStorage = localStorage.getItem('apiKey');
      if (!apiKeyFromStorage && isPatient) {
        console.warn('⚠️ No se encontró API Key. Continuando de todas formas...');
        // NO hacer return - continuar con el flujo para intentar unirse
      }
    }

    // Obtener información de la cita para Droxy
    // IMPORTANTE: Esperar a obtener el API Key del backend antes de unirse a la reunión
    const apiKey = apiKeyFromUrl || localStorage.getItem('apiKey');
    
    console.log('📞 Iniciando llamada a info-cita con appointmentId:', appointmentId);
    
    // Función para iniciar el proceso de unión una vez que tengamos el API Key
    const attemptJoinMeeting = (hasApiKey) => {
      if (!hasApiKey) {
        console.warn('⚠️ No se pudo obtener API Key. No se unirá a la reunión automáticamente.');
        return;
      }

      if (isPatient) {
        console.log('👤 Es paciente, iniciando proceso de unión a la reunión...');
        loadAvailableDevices()
          .then(() => {
            console.log('✅ Dispositivos cargados, esperando 1 segundo antes de unirse...');
            return new Promise(resolve => setTimeout(resolve, 1000));
          })
          .then(() => {
            console.log('⏰ Ejecutando handleJoinMeeting ahora...');
            handleJoinMeeting(appointmentId).catch(err => {
              console.error('❌ Error en handleJoinMeeting:', err);
            });
          })
          .catch((err) => {
            console.error('❌ Error al cargar dispositivos, continuando de todas formas:', err);
            // Intentar unirse incluso si falla la carga de dispositivos
            setTimeout(() => {
              console.log('⏰ Ejecutando handleJoinMeeting después de error en dispositivos...');
              handleJoinMeeting(appointmentId).catch(err => {
                console.error('❌ Error en handleJoinMeeting:', err);
              });
            }, 1000);
          });
      } else {
        console.log('👨‍⚕️ No es paciente, esperando que el doctor inicie la llamada...');
      }
    };

    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/droxy/info-cita/${appointmentId}`, {
      headers: apiKey ? { 'x-api-key': apiKey } : {}
    })
      .then((res) => {
        console.log('📥 Respuesta recibida de info-cita:', {
          success: res.data.success,
          hasApiKey: !!res.data.apiKey,
          hasAppointmentInfo: !!res.data.appointmentInfo,
          responseKeys: Object.keys(res.data)
        });
        
        if (res.data.success) {
          setPatientInfo(res.data.appointmentInfo);
          
          // Si el backend devuelve el apiKey en la respuesta de info-cita, guardarlo
          if (res.data.apiKey) {
            console.log('🔐 API Key recibida del backend en info-cita, guardándola...');
            api.setApiKey(res.data.apiKey);
            localStorage.setItem('apiKey', res.data.apiKey);
            
            // Verificar que se guardó correctamente
            const savedApiKey = localStorage.getItem('apiKey');
            console.log('✅ API Key guardada correctamente:', {
              guardada: !!savedApiKey,
              preview: savedApiKey ? savedApiKey.substring(0, 10) + '...' : 'NO SE GUARDÓ'
            });
            
            // Ahora que tenemos el API Key, intentar unirse a la reunión
            attemptJoinMeeting(true);
          } else {
            // Si ya teníamos API Key desde otra fuente, usar esa
            const existingApiKey = apiKeyFromUrl || apiKeyFromState || localStorage.getItem('apiKey');
            if (existingApiKey) {
              console.log('🔐 Usando API Key existente para unirse a la reunión...');
              attemptJoinMeeting(true);
            } else {
              console.error('❌ No se recibió API Key del backend ni se encontró en otras fuentes');
              alertaGeneral({
                titulo: "Error de autenticación",
                messageHtml: "No se pudo obtener la clave de API necesaria para la videoconsulta. Por favor, verifica la URL o contacta con soporte.",
                isError: true,
              });
              attemptJoinMeeting(false);
            }
          }
        } else {
          throw new Error(res.data.message);
        }
      })
      .catch((err) => {
        console.error("❌ Error al obtener la información de la cita:", err);
        console.error("Detalles del error:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        
        // Si ya teníamos API Key antes de llamar al backend, intentar unirse de todas formas
        const existingApiKey = apiKeyFromUrl || apiKeyFromState || localStorage.getItem('apiKey');
        if (existingApiKey && isPatient) {
          console.warn('⚠️ Error al obtener info-cita, pero tenemos API Key. Intentando unirse...');
          attemptJoinMeeting(true);
        } else {
          alertaGeneral({
            titulo: "Oops...",
            messageHtml: "Ocurrió un error al obtener la información de la cita. Por favor, inténtalo de nuevo.",
            isError: true,
          });
          attemptJoinMeeting(false);
        }
      });
    }, [appointmentId, isPatient, handleJoinMeeting]);

  // useEffect separado para activar cámara cuando los elementos de video estén disponibles
  useEffect(() => {
    if (isPatient && showVideoElements && localVideoRef.current && cameraStatus !== 'active') {
      console.log('🎬 Elementos de video disponibles - activando cámara automáticamente...');
      activateCameraImmediately();
    }
  }, [showVideoElements, isPatient]);

  // useEffect para detectar cuando el video está funcionando y actualizar el estado
  useEffect(() => {
    if (!isPatient) return;
    
    const checkVideoStatus = () => {
      // Verificar ambos elementos de video
      const videos = [
        { ref: localVideoRef, name: 'localVideoRef' },
        { ref: remoteVideoRef, name: 'remoteVideoRef' }
      ];
      
      let anyVideoWorking = false;
      
      for (const { ref, name } of videos) {
        if (ref.current) {
          const video = ref.current;
          if (video.srcObject && !video.paused && video.readyState >= 2 && video.videoWidth > 0) {
            anyVideoWorking = true;
            console.log(`🎯 ${name} detectado como funcionando`);
          }
        }
      }
      
      if (anyVideoWorking && cameraStatus !== 'active') {
        console.log('🎯 Al menos un video detectado como funcionando - actualizando estado inmediatamente');
        setCameraStatus('active');
        setIsVideoEnabled(true);
        
        // Log adicional para confirmar el cambio
        console.log('✅ Estado de cámara actualizado a ACTIVE');
      }
    };
    
    // Verificar inmediatamente
    checkVideoStatus();
    
    // Verificar cada 200ms para una respuesta más rápida
    const interval = setInterval(checkVideoStatus, 200);
    
    return () => clearInterval(interval);
  }, [isPatient, cameraStatus]);

  // useEffect adicional para forzar actualización cuando el video esté listo
  useEffect(() => {
    if (!isPatient || !localVideoRef.current) return;
    
    const video = localVideoRef.current;
    
    // Event listener para cuando el video esté listo
    const handleLoadedMetadata = () => {
      if (video.videoWidth > 0 && cameraStatus !== 'active') {
        console.log('🎬 Video metadata cargado - forzando estado a active');
        setCameraStatus('active');
        setIsVideoEnabled(true);
      }
    };
    
    const handleCanPlay = () => {
      if (video.videoWidth > 0 && cameraStatus !== 'active') {
        console.log('▶️ Video puede reproducirse - forzando estado a active');
        setCameraStatus('active');
        setIsVideoEnabled(true);
      }
    };
    
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [isPatient, cameraStatus]);

  // useEffect específico para PC - activar cámara cuando el layout de escritorio esté listo
  useEffect(() => {
    if (!isPatient || !showVideoElements) return;
    
    // Detectar si estamos en PC (pantalla ancha)
    const isDesktop = window.innerWidth >= 768; // md breakpoint de Tailwind
    
    if (isDesktop) {
      console.log('🖥️ Detectado PC - activando cámara específica para escritorio...');
      
      // Esperar un poco para que el DOM se renderice completamente
      setTimeout(() => {
        console.log('🖥️ Usando estrategia múltiple de asignación...');
        forceCameraWithAllStrategies();
        
        // También intentar con el método específico de PC como respaldo
        setTimeout(() => {
          forceCameraOnPC();
        }, 2000);
      }, 500);
    }
  }, [isPatient, showVideoElements]);

  // Función para verificar y corregir el estado del video periódicamente
  const verifyAndFixVideo = useCallback(async () => {
    if (!isPatient) return;
    
    // Verificar ambos elementos de video para pacientes
    const videos = [
      { ref: localVideoRef, name: 'localVideoRef' },
      { ref: remoteVideoRef, name: 'remoteVideoRef' }
    ];
    
    let anyVideoWorking = false;
    
    for (const { ref, name } of videos) {
      if (ref.current) {
        const video = ref.current;
        const hasStream = !!video.srcObject;
        const isPlaying = !video.paused && video.readyState >= 2;
        
        console.log(`🔍 Verificación periódica del ${name}:`, {
          hasStream,
          isPlaying,
          readyState: video.readyState,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          currentCameraStatus: cameraStatus
        });
        
        if (hasStream && isPlaying && video.videoWidth > 0) {
          anyVideoWorking = true;
        }
      }
    }
    
    // Si cualquier video está funcionando pero el estado no es 'active', actualizarlo INMEDIATAMENTE
    if (anyVideoWorking && cameraStatus !== 'active') {
      console.log('✅ Al menos un video funcionando correctamente - actualizando estado a active INMEDIATAMENTE');
      setCameraStatus('active');
      setIsVideoEnabled(true);
      
      // Forzar re-render para mostrar el estado actualizado
      setTimeout(() => {
        console.log('🔄 Estado actualizado - verificando UI');
      }, 100);
      return;
    }
    
    // Si ningún video tiene stream pero debería tenerlo, intentar asignarlo
    if (!anyVideoWorking && cameraStatus === 'active') {
      console.log('🔄 Ningún video tiene stream pero cámara activa - reasignando...');
      try {
        const stream = await getCameraStream();
        await assignStreamToAllPatientVideos(stream);
      } catch (error) {
        console.error('❌ Error al reasignar stream:', error);
      }
    }
  }, [isPatient, cameraStatus, getCameraStream, assignStreamToAllPatientVideos]);

  // useEffect para verificación periódica
  useEffect(() => {
    if (!isPatient) return;
    
    // Verificar inmediatamente
    verifyAndFixVideo();
    
    // Verificar cada 2 segundos
    const interval = setInterval(verifyAndFixVideo, 2000);
    
    return () => clearInterval(interval);
  }, [isPatient, verifyAndFixVideo]);

  // Efecto para verificar automáticamente la vinculación del video remoto
  useEffect(() => {
    if (!isInMeeting) return;
    
    console.log('🔄 Iniciando verificación automática de vinculación de video remoto...');
    
    // Ejecutar inmediatamente
    checkAndFixRemoteVideoBinding();
    
    // Verificar cada 1.5 segundos si hay tiles remotos y vincularlos
    const intervalId = setInterval(() => {
      if (window.currentMeetingSession) {
        // Verificar si algún elemento remoto no tiene srcObject
        const needsBinding = 
          (remoteVideoRefDesktop.current && !remoteVideoRefDesktop.current.srcObject) ||
          (remoteVideoRefMobile.current && !remoteVideoRefMobile.current.srcObject) ||
          (remoteVideoRef.current && !remoteVideoRef.current.srcObject);
        
        if (needsBinding) {
          console.log('🔄 Verificando y vinculando video remoto (elemento sin srcObject)...');
          checkAndFixRemoteVideoBinding();
        } else {
          // Aún así verificar periódicamente por si hay tiles nuevos
          checkAndFixRemoteVideoBinding();
        }
      }
    }, 1500);
    
    return () => {
      console.log('🛑 Deteniendo verificación automática de vinculación remota');
      clearInterval(intervalId);
    };
  }, [isInMeeting]);

  const handleEndCall = () => {
    console.log('🔴 handleEndCall llamado');
    setVideocall(false);
    setInicio(false);
    
    // Mostrar confirmación de que la llamada ha terminado
    alertaGeneral({
      titulo: "Llamada terminada",
      messageHtml: "La videoconsulta ha sido finalizada.",
    }).then(() => {
      // Navegar de vuelta a la cita temporal
      handleBackToAppointment();
    });
  };

  const handleFinishCall = async () => {
    Swal.fire({
      title: "¿Estás seguro?",
      html: "Recuerda revisar que todos los documentos fueron diligenciados y enviados correctamente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, finalizar",
      cancelButtonText: "Cancelar"
    }).then((result) => {
      if (result.isConfirmed) {
        finalizeAppointment();
      }
    });
  };

  const downloadFile = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const removeKeys = (data, keysToRemove) => {
    if (Array.isArray(data)) {
      return data.map(item => removeKeys(item, keysToRemove));
    } else if (typeof data === "object" && data !== null) {
      const filteredData = { ...data };
      keysToRemove.forEach(key => {
        delete filteredData[key];
      });
      Object.keys(filteredData).forEach(key => {
        filteredData[key] = removeKeys(filteredData[key], keysToRemove);
      });
      return filteredData;
    }
    return data;
  };

  const finalizeAppointment = async () => {
    alertaCargandoProceso({
      titulo: "Finalizando la cita..",
      messageHtml: "Se están guardando los datos de la cita. Por favor, espera un momento",
      funcionAsync: async () => {
        try {
          // Usar endpoint de Droxy para finalizar cita
          const response = await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/api/droxy/finalize-appointment/${appointmentId}`
          );

          if (response.data.success) {
            const { ripsData } = response.data;

            alertaGeneral({
              titulo: "Llamada finalizada con éxito!",
              messageHtml: "La llamada ha sido finalizada correctamente.",
            }).then(() => {
              // Limpiar datos sensibles
              const cleanedData = removeKeys(ripsData, ["_id", "__v", "patientId", "doctorId", "clinicId"]);
              /* downloadFile(cleanedData, "rips_data.json"); // Descargar archivo limpio */
              setShowResume(true);
            });
          } else {
            throw new Error(response.data.message);
          }
        } catch (error) {
          console.error("Error al finalizar la llamada:", error);
          alertaGeneral({
            titulo: "Oops...",
            messageHtml: "Ocurrió un error al finalizar la llamada. Por favor, inténtalo de nuevo.",
            isError: true,
          });
        }
      }
    });
  };

  const handleStartCall = async () => {
    setVideocall(true);
    setInicio(true);
    // Unirse a la reunión cuando el doctor inicia la llamada
    await loadAvailableDevices();
    await handleJoinMeeting(appointmentId);
  };
  
  // Funciones para control de video y audio
  const toggleVideo = async () => {
    if (window.currentMeetingSession) {
      try {
        if (isVideoEnabled) {
          await window.currentMeetingSession.audioVideo.stopLocalVideoTile();
          setIsVideoEnabled(false);
        } else {
          await window.currentMeetingSession.audioVideo.startLocalVideoTile();
          setIsVideoEnabled(true);
        }
      } catch (error) {
        console.error('Error al cambiar estado del video:', error);
      }
    }
  };

  const toggleMute = async () => {
    if (window.currentMeetingSession) {
      try {
        if (isMuted) {
          await window.currentMeetingSession.audioVideo.realtimeUnmuteLocalAudio();
          setIsMuted(false);
        } else {
          await window.currentMeetingSession.audioVideo.realtimeMuteLocalAudio();
          setIsMuted(true);
        }
      } catch (error) {
        console.error('Error al cambiar estado del audio:', error);
      }
    }
  };

  const handleBackToAppointment = () => {
    navigate(`/cita-temporal/${appointmentId}`);
  };

  // Función para verificar dispositivos de media (legacy - puede ser eliminada)
  const checkMediaDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      const audioDevices = devices.filter(device => device.kind === 'audioinput');
      
      console.log('Dispositivos de video disponibles:', videoDevices);
      console.log('Dispositivos de audio disponibles:', audioDevices);
      
      return { videoDevices, audioDevices };
    } catch (error) {
      console.error('Error al enumerar dispositivos:', error);
      return { videoDevices: [], audioDevices: [] };
    }
  };

  // Log para depurar el render
  console.log('🎬 Render VideoCallDroxy:', {
    showResume,
    showVideoElements,
    isInMeeting,
    videocall,
    inicio,
    isPatient,
    cameraStatus,
    isVideoEnabled
  });

  // Debugging específico para PC
  useEffect(() => {
    if (isPatient && showVideoElements) {
      console.log('🖥️ DEBUGGING ESPECÍFICO PARA PC:');
      console.log('📱 isPatient:', isPatient);
      console.log('📱 showVideoElements:', showVideoElements);
      console.log('📱 cameraStatus:', cameraStatus);
      console.log('📱 isVideoEnabled:', isVideoEnabled);
      
      // Verificar elementos de video en PC
      const desktopLayout = document.querySelector('.hidden.md\\:flex');
      if (desktopLayout) {
        console.log('🖥️ Layout de escritorio encontrado');
        
        const videos = desktopLayout.querySelectorAll('video');
        console.log('🖥️ Videos encontrados en layout de escritorio:', videos.length);
        
        videos.forEach((video, index) => {
          console.log(`🖥️ Video ${index + 1}:`, {
            ref: video.getAttribute('ref') || 'sin ref',
            srcObject: !!video.srcObject,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            readyState: video.readyState,
            paused: video.paused,
            muted: video.muted,
            className: video.className,
            parentElement: video.parentElement?.className
          });
        });
      } else {
        console.log('🖥️ Layout de escritorio NO encontrado');
      }
      
      // Verificar referencias
      console.log('🖥️ Referencias de video:');
      console.log('  localVideoRef.current:', !!localVideoRef.current);
      console.log('  remoteVideoRef.current:', !!remoteVideoRef.current);
      
      if (localVideoRef.current) {
        console.log('  localVideoRef details:', {
          srcObject: !!localVideoRef.current.srcObject,
          videoWidth: localVideoRef.current.videoWidth,
          videoHeight: localVideoRef.current.videoHeight,
          readyState: localVideoRef.current.readyState
        });
      }
      
      if (remoteVideoRef.current) {
        console.log('  remoteVideoRef details:', {
          srcObject: !!remoteVideoRef.current.srcObject,
          videoWidth: remoteVideoRef.current.videoWidth,
          videoHeight: remoteVideoRef.current.videoHeight,
          readyState: remoteVideoRef.current.readyState
        });
      }
    }
  }, [isPatient, showVideoElements, cameraStatus, isVideoEnabled]);

  return (
    <>
      {/* <ConfigCheck /> Removido para producción */}
      <MenuNav 
        isPatient={isPatient} 
        showResume={showResume} 
        inicio={inicio} 
        handleFinishCall={handleFinishCall} 
        appointmentId={appointmentId} 
        patientInfo={patientInfo}
        isDroxy={true}
        onBackToAppointment={handleBackToAppointment}
      >
      {showResume && (
        <div className="flex items-center justify-center w-full h-[85vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Cita Finalizada</h2>
            <p className="text-gray-600 mb-6">La videoconsulta ha sido completada exitosamente.</p>
            <button
              onClick={handleBackToAppointment}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Volver a la Cita
            </button>
          </div>
        </div>
      )}

      {!showResume && (showVideoElements || isInMeeting || videocall) ? (
        <div className="flex flex-col w-full h-[92vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          {/* Video Area - Responsive Layout */}
          <div className="flex-1 relative overflow-hidden p-0 md:p-0">
            {/* Desktop Layout - Main + Overlay (matching mobile) */}
            <div className="hidden md:block h-full relative rounded-xl overflow-hidden">
              {/* Main (full) */}
              <div className="absolute inset-0 bg-gray-800">
                <video
                  data-self={!isPatient}
                  ref={isPatient ? remoteVideoRefDesktop : localVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  style={{ minHeight: '320px', backgroundColor: '#000' }}
                />
                <div className="absolute bottom-3 left-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg backdrop-blur-sm">
                  {isPatient ? 'Doctor' : 'Tú'}
                </div>
                {!isVideoEnabled && !isPatient && (
                  <div className="absolute inset-0 bg-gray-700/70 flex items-center justify-center">
                    <div className="text-white text-center">
                      <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-2xl">👤</span>
                      </div>
                      <p className="text-sm">Cámara desactivada</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Overlay (small) */}
              <div className="absolute top-4 right-4 w-40 h-40 bg-gray-900 rounded-xl overflow-hidden shadow-2xl border-2 border-green-500 z-50 p-1" data-mosaic="overlay">
                <video
                  data-self={isPatient}
                  ref={isPatient ? localVideoRef : (remoteVideoRefDesktop || remoteVideoRef)}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-contain bg-black"
                />
                <div className="absolute bottom-2 left-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-lg backdrop-blur-sm">
                  {isPatient ? 'Tú' : 'Paciente'}
                </div>
              </div>
            </div>

            {/* Mobile Layout - Stacked */}
            <div className="md:hidden flex flex-col h-full">
              <div className="flex-1 relative">
                {/* Main Video */}
                <div className="absolute inset-0 bg-gray-800 rounded-xl overflow-hidden shadow-2xl border-2 border-gray-700">
                  <video
                    data-self={!isPatient}
                    ref={isPatient ? remoteVideoRefMobile : localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-3 left-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg backdrop-blur-sm">
                    {isPatient ? 'Doctor' : 'Tú'}
                  </div>
                </div>

                {/* Overlay Video */}
                <div className="absolute top-4 right-4 w-28 h-36 bg-gray-800 rounded-xl overflow-hidden shadow-2xl border-2 border-green-500 z-10 hover:scale-105 transition-transform duration-300">
                  <video
                    data-self={isPatient}
                    ref={isPatient ? localVideoRef : (remoteVideoRefMobile || remoteVideoRef)}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 left-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-lg backdrop-blur-sm">
                    {isPatient ? 'Tú' : 'Paciente'}
                  </div>
                  
                  {/* Indicador de estado de cámara para pacientes (móvil) */}
                  {isPatient && (
                    <div className="absolute top-1 right-1">
                      {cameraStatus === 'checking' && (
                        <div className="bg-yellow-500 text-white px-1 py-0.5 rounded text-xs animate-pulse">
                          🔄
                        </div>
                      )}
                      {cameraStatus === 'active' && (
                        <div className="bg-green-500 text-white px-1 py-0.5 rounded text-xs">
                          📹
                        </div>
                      )}
                      {cameraStatus === 'denied' && (
                        <div className="bg-red-500 text-white px-1 py-0.5 rounded text-xs">
                          ❌
                        </div>
                      )}
                      {cameraStatus === 'error' && (
                        <div className="bg-red-500 text-white px-1 py-0.5 rounded text-xs">
                          ⚠️
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Controls: solo micrófono y cámara */}
          <div className="bg-white border-t border-gray-200 p-3 md:p-4">
            <div className="flex items-center justify-center gap-4 md:gap-6">
              <button
                type="button"
                onClick={toggleMute}
                className={`h-12 w-12 md:h-14 md:w-14 flex items-center justify-center rounded-full transition-colors ${
                  isMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                } text-white shadow-lg`}
                aria-label={isMuted ? 'Activar micrófono' : 'Silenciar micrófono'}
                title={isMuted ? 'Activar micrófono' : 'Silenciar micrófono'}
              >
                {isMuted ? '🔇' : '🎤'}
              </button>
              <button
                type="button"
                onClick={toggleVideo}
                className={`h-12 w-12 md:h-14 md:w-14 flex items-center justify-center rounded-full transition-colors ${
                  !isVideoEnabled ? 'bg-gray-600 hover:bg-gray-700' : 'bg-green-600 hover:bg-green-700'
                } text-white shadow-lg`}
                aria-label={isVideoEnabled ? 'Apagar cámara' : 'Encender cámara'}
                title={isVideoEnabled ? 'Apagar cámara' : 'Encender cámara'}
              >
                {!isVideoEnabled ? '📹❌' : '📹'}
              </button>
            </div>
          </div>
            </div>
      ) : !showResume && !isPatient && (
        <div className="flex items-center justify-center w-full h-[85vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Videoconsulta Droxy</h2>
            <p className="text-gray-600 mb-6">Cita ID: {appointmentId}</p>
            <div className="flex flex-col gap-4">
              <button
                className="flex text-white bg-[#0aad5cff] hover:bg-[#09944eff] px-6 py-3 border border-[#6e787aff] rounded-md mx-auto"
                onClick={handleStartCall}
              >
                Iniciar VideoConsulta
              </button>
              <button
                className="flex text-white bg-blue-600 hover:bg-blue-700 px-6 py-3 border border-blue-700 rounded-md mx-auto"
                onClick={() => setShowCameraTest(true)}
              >
                Probar Cámara
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pantalla de carga para pacientes */}
      {!showResume && isPatient && !showVideoElements && !isInMeeting && !videocall && (
        <div className="flex items-center justify-center w-full h-[85vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Preparando Videoconsulta</h2>
            <p className="text-gray-600 mb-6">Cita ID: {appointmentId}</p>
            
            {/* Estado de la cámara */}
            <div className="mb-6">
              {cameraStatus === 'checking' && (
                <div className="flex items-center justify-center space-x-2 text-blue-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span>Activando cámara...</span>
                </div>
              )}
              {cameraStatus === 'active' && (
                <div className="flex items-center justify-center space-x-2 text-green-600">
                  <span className="text-2xl">📹</span>
                  <span>Cámara activa</span>
                </div>
              )}
              {cameraStatus === 'denied' && (
                <div className="flex items-center justify-center space-x-2 text-red-600">
                  <span className="text-2xl">❌</span>
                  <span>Cámara denegada</span>
                </div>
              )}
              {cameraStatus === 'error' && (
                <div className="flex items-center justify-center space-x-2 text-red-600">
                  <span className="text-2xl">⚠️</span>
                  <span>Error de cámara</span>
                </div>
              )}
            </div>
            
            {/* Botones de acción (solo activar cámara y error) */}
            <div className="flex flex-col gap-3">
              <button
                onClick={activateCameraImmediately}
                className="flex text-white bg-green-600 hover:bg-green-700 px-6 py-3 border border-green-700 rounded-md mx-auto"
              >
                {cameraStatus === 'checking' ? 'Activando...' : 'Activar Cámara'}
              </button>
              {cameraError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mx-auto max-w-md">
                  <p className="text-sm">{cameraError}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat compacto flotante */}
      {showChat && (
        <div className="fixed bottom-2 right-2 z-40 w-48 md:w-56 max-h-[30vh] bg-white border border-[#6e787aff] rounded-lg shadow-md flex flex-col overflow-hidden">
          <h1 className="w-full flex items-center justify-between px-1.5 py-1 text-xs font-semibold text-[#70706eff] bg-gray-50 border-b border-[#6e787aff]">
            <span className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" className="text-[#1f94c2ff] w-3.5 h-3.5 mr-1.5" fill="currentColor" stroke="currentColor"><path d="M150,5C70,5,5,70,5,150s65,145,145,145s145-65,145-145S230,5,150,5z M150,281.2c-72.3,0-131.2-58.9-131.2-131.2 S77.7,18.8,150,18.8S281.2,77.7,281.2,150S222.3,281.2,150,281.2z M210.7,81H89.3c-10.7,0-19.4,8.7-19.4,19.4 c0,0,0.1,84.5,0.1,114.3c0,3.8,4.6,5.7,7.2,3l26.8-26.8h106.7c10.7,0,19.4-8.7,19.4-19.4v-71.1C230.1,89.7,221.4,81,210.7,81z  M197.5,149.8c-0.8,0.8-1.9,1.3-3.2,1.3h-86.7c-2.5,0-4.5-2-4.5-4.5c0-1.2,0.5-2.4,1.3-3.2c0.8-0.8,1.9-1.3,3.2-1.3h86.7 c2.5,0,4.5,2,4.5,4.5C198.8,147.8,198.3,149,197.5,149.8z M197.5,125.2c-0.8,0.8-1.9,1.3-3.2,1.3h-86.7c-2.5,0-4.5-2-4.5-4.5 c0-1.2,0.5-2.4,1.3-3.2c0.8-0.8,1.9-1.3,3.2-1.3h86.7c2.5,0,4.5,2,4.5,4.5C198.8,123.2,198.3,124.4,197.5,125.2z"/></svg>
              Chat
            </span>
            <button onClick={() => setShowChat(false)} className="text-[10px] text-[#70706eff] hover:text-[#1f94c2ff]">Minimizar</button>
          </h1>
          <div className="w-full flex-1 overflow-y-auto p-1">
            <ul className="space-y-1" />
          </div>
          <div className="w-full border-t border-[#6e787aff] p-1">
            <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); }}>
              <input type="text" placeholder="Escribe el mensaje" className="form-input px-1.5 py-0.5 text-[11px] flex-1" />
              <button type="submit" className="button-send px-2 py-0.5 text-[11px]">Enviar</button>
            </form>
          </div>
        </div>
      )}

      {/* Componente de prueba de cámara */}
      {showCameraTest && (
        <CameraTest
          onTestComplete={() => setShowCameraTest(false)}
        />
      )}

      </MenuNav>
    </>
  );
};

export default VideoCallDroxy; 